import type {
  RegistrantLicenseListLicenseResponseSchema,
  RegistrantLicenseListQuestionColumnSchema,
} from "@/hera-api/client/types.gen";
import {
  getOmtAmsLicenseFilterValues,
  getOmtAmsLicenseMapPins,
  getOmtAmsLicenses,
} from "@/hera-api/omt-ams-api";
import { dashboardListQueryOptions } from "@/lib/dashboard-query-options";
import {
  BACKGROUND_QUERY_META,
  isForegroundFetch,
} from "@/lib/foreground-fetch";
import { setLicenseDisplayedSnapshot } from "@/lib/license-displayed-snapshot";
import type { MapCluster, MapViewport } from "@/lib/map-clusters";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";

import {
  type LicenseListSortDir,
  serializeLicenseListFilters,
} from "./use-paginated-non-registrant-licenses";
import type { RegistrantLicenseListTab } from "./use-paginated-registrant-licenses";
import { usePrefetchAdjacentListPages } from "./use-prefetch-adjacent-list-pages";

const EMPTY_LICENSES: RegistrantLicenseListLicenseResponseSchema[] = [];
const EMPTY_QUESTION_COLUMNS: RegistrantLicenseListQuestionColumnSchema[] = [];

export function usePaginatedOmtAmsLicenses({
  page,
  pageSize,
  sortBy,
  sortDir,
  columnFilters,
  tab,
  bulkPaymentCohort,
  enabled,
}: {
  page: number;
  pageSize: number;
  sortBy: string;
  sortDir: LicenseListSortDir;
  columnFilters: Record<string, string[]>;
  tab?: RegistrantLicenseListTab;
  bulkPaymentCohort?: boolean;
  enabled: boolean;
}) {
  const filtersJson = serializeLicenseListFilters(columnFilters);
  const query = useMemo(
    () => ({
      page,
      page_size: pageSize,
      sort_by: sortBy,
      sort_dir: sortDir,
      filters: filtersJson,
      tab,
      bulk_payment_cohort: bulkPaymentCohort || undefined,
    }),
    [page, pageSize, sortBy, sortDir, filtersJson, tab, bulkPaymentCohort],
  );

  const queryClient = useQueryClient();
  const result = useQuery({
    queryKey: ["omt-ams-licenses", query],
    queryFn: () => getOmtAmsLicenses(query),
    enabled,
    ...dashboardListQueryOptions,
  });

  const prefetchPage = useCallback(
    (adjacentPage: number) => {
      void queryClient.prefetchQuery({
        queryKey: ["omt-ams-licenses", { ...query, page: adjacentPage }],
        queryFn: () => getOmtAmsLicenses({ ...query, page: adjacentPage }),
        ...dashboardListQueryOptions,
        meta: BACKGROUND_QUERY_META,
      });
    },
    [query, queryClient],
  );

  usePrefetchAdjacentListPages({
    enabled,
    isFetching: result.isFetching,
    page,
    pageSize,
    totalCount: result.data?.total_count ?? 0,
    prefetchPage,
  });

  const licenses = result.data?.licenses ?? EMPTY_LICENSES;

  useEffect(() => {
    if (!enabled) return;
    setLicenseDisplayedSnapshot({
      pageLicenseIds: licenses.map((row) => row.id),
      pageJlts: licenses.map((row) => ({
        licenseId: row.id,
        jurisdictionId: row.jurisdiction.id,
        licenseTypeId: row.license_type.id,
        registrantAccountId: row.registrant_account_id ?? null,
      })),
    });
  }, [enabled, licenses]);

  return {
    licenses,
    totalCount: result.data?.total_count ?? 0,
    questionColumns: result.data?.question_columns ?? EMPTY_QUESTION_COLUMNS,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    isForegroundFetching: isForegroundFetch(result),
    error: result.error,
    dataUpdatedAt: result.dataUpdatedAt,
    refetch: result.refetch,
  };
}

export function useOmtAmsLicenseMapPins({
  columnFilters,
  tab,
  bulkPaymentCohort,
  enabled,
  viewport = null,
}: {
  columnFilters: Record<string, string[]>;
  tab?: RegistrantLicenseListTab;
  bulkPaymentCohort?: boolean;
  enabled: boolean;
  viewport?: MapViewport | null;
}) {
  const filtersJson = serializeLicenseListFilters(columnFilters);
  const result = useQuery({
    queryKey: [
      "omt-ams-map-pins",
      filtersJson,
      tab,
      bulkPaymentCohort,
      viewport,
    ],
    queryFn: () =>
      getOmtAmsLicenseMapPins({
        tab,
        filters: filtersJson,
        bulk_payment_cohort: bulkPaymentCohort || undefined,
        north: viewport?.north,
        south: viewport?.south,
        east: viewport?.east,
        west: viewport?.west,
        zoom: viewport?.zoom,
      }),
    enabled,
    ...dashboardListQueryOptions,
  });

  const pins = result.data?.pins ?? [];
  const clusters = (result.data?.clusters ?? []) as MapCluster[];
  const mode = result.data?.mode ?? "pins";

  useEffect(() => {
    if (!enabled) {
      setLicenseDisplayedSnapshot({
        pinLicenseIds: [],
        pinJlts: [],
        mapIsClustered: false,
      });
      return;
    }
    setLicenseDisplayedSnapshot({
      pinLicenseIds: mode === "pins" ? pins.map((pin) => pin.id) : [],
      pinJlts:
        mode === "pins"
          ? pins.map((pin) => ({
              licenseId: pin.id,
              jurisdictionId: pin.jurisdiction_id,
              licenseTypeId: pin.license_type_id,
            }))
          : [],
      mapIsClustered: mode === "clusters",
    });
  }, [enabled, mode, pins]);

  return {
    pins,
    clusters,
    mode,
    totalInView: result.data?.total_in_view ?? pins.length,
    isFetching: result.isFetching,
    isForegroundFetching: isForegroundFetch(result),
  };
}

export async function fetchOmtAmsLicenseFilterValues(args: {
  column: string;
  columnFilters: Record<string, string[]>;
  tab?: RegistrantLicenseListTab;
  bulkPaymentCohort?: boolean;
  search?: string;
}): Promise<string[]> {
  const data = await getOmtAmsLicenseFilterValues({
    column: args.column,
    filters: serializeLicenseListFilters(args.columnFilters),
    tab: args.tab,
    search: args.search?.trim() || undefined,
    bulk_payment_cohort: args.bulkPaymentCohort || undefined,
  });
  return data?.values ?? [];
}
